import { Button, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import { BudgetSuggestionRow } from 'src/Components/Expenses/BudgetSuggestionRow';
import { formatEuros } from 'src/Utils/budgetWizardMath';
import { getCategoryLabel } from 'src/Utils/expenseCategories';

import styles from './DailyLifeEnvelopeStep.module.scss';
import { Props } from './DailyLifeEnvelopeStep.types';

const DailyLifeEnvelopeStep = ({ previousYear, draft, suggestion, onChange }: Props) => {
  const { t } = useTranslation();
  const [isDetailOpen, detail] = useDisclosure(false);

  const takeOverPreviousYear = () => {
    onChange({ ...draft, enabled: true, amount: suggestion.previousYearTotal });
    detail.close();
  };

  return (
    <div className={styles.container}>
      <BudgetSuggestionRow
        checked={draft.enabled}
        onCheckedChange={enabled => onChange({ ...draft, enabled })}
        title={t('budgetV2.dailyLife.envelope', 'Enveloppe annuelle')}
        meta={
          suggestion.previousYearTotal > 0
            ? t('budgetV2.dailyLife.spentIn', 'Dépensé en {{year}} : {{amount}}', {
                year: previousYear,
                amount: formatEuros(suggestion.previousYearTotal),
              })
            : t('budgetV2.dailyLife.noPrevious', 'Rien constaté en {{year}}', {
                year: previousYear,
              })
        }
        amountLabel={t('budgetV2.amountLabel', 'Montant')}
        amount={draft.amount}
        onAmountChange={amount => onChange({ ...draft, amount })}
      />

      <Button variant="outline" size="md" onClick={detail.open}>
        {t('budgetV2.dailyLife.seeDetail', 'Voir le détail {{year}}', { year: previousYear })}
      </Button>

      <Modal
        opened={isDetailOpen}
        onClose={detail.close}
        title={t('budgetV2.dailyLife.detailTitle', 'Vie quotidienne — dépensé en {{year}}', {
          year: previousYear,
        })}
        centered
      >
        {suggestion.bySubCategory.length === 0 ? (
          <p className={styles.empty}>
            {t('budgetV2.dailyLife.detailEmpty', 'Aucune dépense de vie quotidienne en {{year}}.', {
              year: previousYear,
            })}
          </p>
        ) : (
          suggestion.bySubCategory.map(entry => (
            <div key={entry.subCategory}>
              <div className={styles.detailRow}>
                <span>{getCategoryLabel('DAILY_LIFE', entry.subCategory)}</span>
                <b>{formatEuros(entry.previousYearAmount)}</b>
              </div>
              {/* La rubrique se détaille catégorie par catégorie, comme les Frais fixes :
                  « Vie quotidienne 1 000 € » ne dit pas où est passé l'argent. */}
              {(entry.byCategory ?? []).map(category => (
                <div key={category.category} className={styles.categoryRow}>
                  <span>{t(`expense.type.${category.category}`)}</span>
                  <span>{formatEuros(category.previousYearAmount)}</span>
                </div>
              ))}
            </div>
          ))
        )}

        <div className={styles.detailTotal}>
          <span>
            {t('budgetV2.dailyLife.detailTotal', 'Total {{year}}', { year: previousYear })}
          </span>
          <b>{formatEuros(suggestion.previousYearTotal)}</b>
        </div>

        <div className={styles.modalActions}>
          <Button variant="outline" size="md" onClick={detail.close}>
            {t('common.close', 'Fermer')}
          </Button>
          <Button
            size="md"
            onClick={takeOverPreviousYear}
            disabled={suggestion.previousYearTotal === 0}
          >
            {suggestion.previousYearTotal === 0
              ? t('budgetV2.dailyLife.nothingToTakeOver', 'Rien à reprendre pour {{year}}', {
                  year: previousYear,
                })
              : t('budgetV2.dailyLife.takeOver', 'Reprendre {{amount}}', {
                  amount: formatEuros(suggestion.previousYearTotal),
                })}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DailyLifeEnvelopeStep;
